<template>
    <v-container fluid>
        <v-row>
            <v-col
                v-for="field in metadataFields"
                :key="field.field"
                cols="12"
                lg="4"
                sm="6"
                md="6"
            >
                <v-text-field
                    v-if="field.type === 'text'"
                    :label="field.label"
                    v-model="bindings[field.field]"
                    :disabled="!canManageMetadata"
                />
                <v-text-field
                    v-else-if="field.type === 'number'"
                    :label="field.label"
                    v-model.number="bindings[field.field]"
                    type="number"
                    :disabled="!canManageMetadata"
                />
                <v-autocomplete
                    v-else-if="field.type === 'select'"
                    :label="field.label"
                    v-model="bindings[field.field]"
                    :items="field.options"
                    item-title="label"
                    item-value="value"
                    clearable
                    :multiple="field.multiple"
                    :disabled="!canManageMetadata"
                />
            </v-col>
        </v-row>
    </v-container>
</template>

<script setup lang="ts">
import {
    ExperimentMetadataField,
    experimentsAPI,
    MetadataConfigContext,
} from "@/services/experiments/experimentsAPI";
import { ref, onMounted, computed } from "vue";
import { useAuth } from "@/lib/ts/auth";

const props = defineProps<{
    modelValue: Record<string, any>;
    metadataContext: MetadataConfigContext;
    includeGeoCoordinateFields?: boolean;
}>();

const emit = defineEmits<{
    (e: "update:modelValue", value: Record<string, any>): void;
}>();

const auth = useAuth();
const canManageMetadata = computed(() => {
    return auth.isAuthorized("Experiments:ManageV2Metadata");
});

const metadataFields = ref<ExperimentMetadataField[]>([]);
const originalModelValue = ref<Record<string, any>>({ ...props.modelValue });

const bindings = ref<Record<string, any>>({});

onMounted(async () => {
    metadataFields.value = await experimentsAPI.getMetadataConfig(
        props.metadataContext,
        props.includeGeoCoordinateFields || false
    );

    metadataFields.value.forEach((field) => {
        bindings.value[field.field] = computed({
            get: () => props.modelValue[field.field],
            set: (val) => {
                emit("update:modelValue", {
                    ...props.modelValue,
                    [field.field]: val,
                });
            },
        });
    });
});

function reset() {
    bindings.value = { ...originalModelValue.value };
    emit("update:modelValue", originalModelValue.value);
}

defineExpose({
    reset,
});
</script>
